import EventEmitter from 'lib/helpers/EventEmmiter';
import HelpService from "../service/help.service";
import { SCREEN_SHARE } from 'a_root-folder/constants';

/**
 * on('e3-webrtc-send-invite-data', data) - event for sent offer or answer to interlocutor
 * @param {data} offer or answer from Peerconnection
 *
 * on('e3-webrtc-remove-peer', peerID) - event for remove peerConnection
 * @param {peerID} id of Peerconnection
 *
 * on('e3-webrtc-add-local-camera', stream) - event for connect localCamer
 * @param {stream} stream from local camera
 *
 * on('e3-webrtc-on-track', evt, peerID) - event for add remote camer
 * @param {evt} stream from remote camera
 * @param {peerID} id of Peerconnection
 *
 * on('e3-webrtc-codec-error', err) - codec error
 * @param {err} codec error
 *
 */

export default class e3WebRTC extends EventEmitter {
    constructor(local_stream) {
        super();
        this._local_stream = local_stream;
        this._pc = [];
        this._is_Edge = /Edge\/\d./i.test(navigator.userAgent);
        this._localCameraConnect = false;
        this._helperService = new HelpService();
        this._permissions = this._helperService.getPermissions();
        this._enableConsoleLog = this._permissions['debug'];
        this._waitForTrack = {};
    }

    subscribeRTC(peerID, initiator, stream) {
        return this._subscribeRTC(peerID, initiator, stream);
    }

    _subscribeRTC(peerID, initiator, stream) {
        let localStream = this._local_stream;
        if(stream) localStream = stream;

        this._logger("%c%s Create connection with peer - %s. Type - %s", 'color: blue', new Date(), peerID, initiator ? 'initiator' : 'Client');
        this._pc[peerID] = new RTCPeerConnection({
            'iceServers': [{
                'urls': ['turn:rtc.eyespy360.com:80?transport=udp', 'stun:rtc.eyespy360.com:80', 'turn:rtc.eyespy360.com:443?transport=tcp', 'turns:rtc.eyespy360.com:443?transport=tcp'],
                username: 'user',
                credential: 'root'
            }]
        });
        this._pc[peerID].initiator = initiator;
        this._pc[peerID].candidate = [];
        this._pc[peerID].waitForLastCandidate = null;

        this._pc[peerID].onnegotiationneeded = function () {
            if (this._pc[peerID].initiator) {
                this._logger("%c%s Create offer to - %s", 'color: blue', new Date(), peerID);
                this._pc[peerID].createOffer()
                    .then(function (offer) {
                        return this._pc[peerID].setLocalDescription(offer);
                    }.bind(this))
                    .catch((err) => {
                        console.error(err)
                        this._logger(err);
                    });
            }
        }.bind(this);

        this._pc[peerID].onicecandidate = function (evt) {
            if (evt.candidate) {
                this._pc[peerID].candidate.push(evt.candidate)
            } else {
                if (this._pc[peerID] && this._pc[peerID].waitForLastCandidate && this._pc[peerID].waitForLastCandidate != 'sent') {
                    clearTimeout(this._pc[peerID].waitForLastCandidate);
                    this._pc[peerID].waitForLastCandidate = 'sent';
                    this._sendInviteData(peerID);
                }
            }

            if (this._pc[peerID] && !this._pc[peerID].waitForLastCandidate) {
                this._pc[peerID].waitForLastCandidate = setTimeout(function (peerID) {
                    if (this._pc[peerID])
                        this._pc[peerID].waitForLastCandidate = 'sent';
                    this._sendInviteData(peerID);
                }.bind(this, peerID), 5000)
            }
        }.bind(this);

        this._pc[peerID].oniceconnectionstatechange = function () {
            if (this._pc[peerID] && ['failed', 'disconnected', 'closed'].indexOf(this._pc[peerID].iceConnectionState) > -1) {
                this._logger("%s %s candidate - %s", new Date(), this._pc[peerID].iceConnectionState, peerID);
                this._removePeer(peerID);
            }
        }.bind(this);

        this._pc[peerID].onsignalingstatechange = function () {
            this._logger('onsignalingstatechange', this._pc[peerID].signalingState);
        }.bind(this);

        this._pc[peerID].onicegatheringstatechange = function (evt) {
            let label = "Unknown";
            if (!this._pc[peerID]) {
                return this._logger('onicegatheringstatechange undefind', peerID);
            }

            switch (this._pc[peerID].iceGatheringState) {
                case "new":
                case "complete":
                    label = "Idle";
                    break;
                case "gathering":
                    label = "Determining route";
                    break;
            }
            this._logger('onicegatheringstatechange', label);

            // if(this._pc[peerID] && this._pc[peerID].iceGatheringState === 'complete'){
            //     console.log('ice gather complete', this._pc[peerID].candidate.length);
            //     this._sendInviteData(peerID);
            // }
        }.bind(this);

        this._pc[peerID].ontrack = function (evt) {
            // evt.stream = evt.streams[0];

            this._fireEvent("e3-webrtc-on-track", evt, peerID);
        }.bind(this);

        this._pc[peerID].addStream(localStream);
    }

    _connectLocalCamera() {
        if (!this._localCameraConnect) {
            this._fireEvent('e3-webrtc-add-local-camera', this._local_stream);
            this._localCameraConnect = true;
        }
    }

    leavePeer(id) {
        this._logger("%s Left: %s", new Date(), id);
        if (this._pc[id]) {
            this._pc[id].close();
            delete this._pc[id];
        }
    }

    removeScreenSharePeer() {
        for(const id in this._pc){
            if(id.match(/SCREEN_SHARE/)){
                if (this._pc[id]) {
                    this._pc[id].close();
                    delete this._pc[id];
                }
            }
        }
    }

    connectToPeer(peerID, initiator, stream) {
        this._logger("%s Joined: %s", new Date(), peerID);
        this._connectLocalCamera();
        if (!this._pc[peerID]) {
            try {
                this._subscribeRTC(peerID, initiator, stream);
            } catch (e) {
                console.error(e);
                this._fireEvent('e3-webrtc-codec-error', e);
            }
        }
    }

    sendRTCData(res, stream) {
        var data = res.data;
        var peerID = data.publisher;

        this.connectToPeer(peerID, !data.message.initiator, stream);
        this._setRTCdata(data);
    }

    _setRTCdata(data) {
        var message = data.message;
        var peerID = data.publisher;

        if (message.desc) {
            var desc = message.desc;
            this._logger("%c%s Get message from: %s , Type: %s", 'color: blue', new Date, peerID, desc.type);
            if (desc.type == "offer") {
                this._logger(desc.sdp)
                this._pc[peerID].setRemoteDescription(desc).then(function () {
                    this._logger('Get message from: ' + peerID + ' setRemoteDescription');
                    if (this._pc[peerID].remoteDescription.type == "offer")
                        return this._pc[peerID].createAnswer();
                }.bind(this)).then(function (answer) {
                    this._logger('Get message from: ' + peerID + ' createAnswer');
                    this._pc[peerID].setLocalDescription(answer);
                    return answer;
                }.bind(this)).then(function (answer) {
                    this._logger('Get message from: ' + peerID + ' setLocalDescription');
                    this._addIceCandidate(message, peerID);
                    this._pc[peerID].answer = answer;
                }.bind(this)).catch(function (err) {
                    this._logger(err);
                    if (err.name === 'OperationError') {
                        this._fireEvent('e3-webrtc-codec-error', err);
                    }
                }.bind(this));

            } else if (desc.type == "answer") {
                this._logger("%c%s setRemoteDescription to - %s", 'color: blue', new Date, peerID);
                this._pc[peerID].setRemoteDescription(desc).catch((err) => {
                    console.error(err);
                    this._logger(err);
                });
                this._addIceCandidate(message, peerID);
            } else {
                this._logger("%s Unsupported SDP type. Your code may differ here.", new Date());
            }
        }
    }

    _sendInviteData(peerID) {
        if (this._pc[peerID].initiator) {
            this._fireEvent('e3-webrtc-send-invite-data', {
                candidate: this._pc[peerID].candidate,
                desc: this._pc[peerID].localDescription,
                to: peerID,
                initiator: this._pc[peerID].initiator
            });

        } else {
            this._fireEvent('e3-webrtc-send-invite-data', {
                candidate: this._pc[peerID].candidate,
                desc: this._pc[peerID].answer,
                to: peerID,
                initiator: this._pc[peerID].initiator
            });
        }
    }

    _addIceCandidate(message, peerID) {
        var candidates = message.candidate;
        this._logger("%c%s Get and add IceCandidates to - %s, count - %s", 'color: blue', new Date(), peerID, candidates.length);
        this._logger(candidates);

        for (var i = 0; i < candidates.length; i++) {
            this._pc[peerID].addIceCandidate(candidates[i]).catch(function (err) {
                console.error(err);
                this._logger(err);
            });
        }

        setTimeout(() => {
            this._logger('send null candidate');
            this._pc[peerID].addIceCandidate(null).catch(function (err) {
                console.error(err);
                this._logger(err);
            });
        }, 500)
    }

    _removePeer(peerID) {
        delete this._pc[peerID];
        this._fireEvent('e3-webrtc-remove-peer', peerID);
    }

    _logger(data) {
        if (this._enableConsoleLog) console.info.apply(console, arguments);
    }
}
